import posthog from 'posthog-js';

const POSTHOG_KEY = "phc_RsHSxsff07YRxx92pNJw6QyNwrqhcF6HX7WrpH4R4bM";

/**
 * Decode a JWT payload client-side (no signature verification — the server
 * still validates the token for real auth; we only need email for identify).
 */
function decodeJwtPayload(token) {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Read the persisted session identity (email) from the auth token in
 * localStorage, synchronously, with no API call. Returns null if there is no
 * token, it cannot be decoded, it has no email, or it is expired.
 */
function getPersistedIdentity() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload?.email) return null;
    if (payload.exp && Date.now() / 1000 > payload.exp) return null;
    return {
      email: payload.email,
      is_subscribed: payload.is_subscribed || false,
    };
  } catch {
    return null;
  }
}

/**
 * Identify the already-authenticated user from persisted session state.
 * Runs inside posthog.init's loaded callback, before the automatic $pageview,
 * so returning users' pageviews are attributed to their person profile.
 */
function identifyFromPersistedSession(identity) {
  try {
    const isProduction = process.env.NODE_ENV === 'production';
    const isTestMode = localStorage.getItem('testPostHog') === 'true';
    if ((isProduction || isTestMode) && posthog.has_opted_in_capturing()) {
      posthog.identify(identity.email, {
        email: identity.email,
        is_subscribed: identity.is_subscribed,
      });
      if (isTestMode) {
        console.log('[PostHog] Identified from persisted session:', identity.email);
      }
    }
  } catch (error) {
    console.warn('[PostHog] Failed to identify from persisted session:', error.message);
  }
}

/**
 * Initialize PostHog with memory persistence (no cookies).
 * Tracks events for conversion funnel before consent; switches to cookie when user consents.
 */
export const initializePostHog = () => {
  try {
    if (posthog.__loaded) {
      applyConsentState();
      return;
    }

    const persistedIdentity = getPersistedIdentity();

    posthog.init(POSTHOG_KEY, {
      api_host: "https://us.i.posthog.com",
      person_profiles: "identified_only",
      persistence: "memory", // No cookies until consent; enables pre-consent conversion tracking
      opt_out_capturing_by_default: false,
      // Attribute the very first event (including the automatic $pageview) to
      // the returning user's distinct_id, before any async auth check resolves.
      ...(persistedIdentity
        ? { bootstrap: { distinctID: persistedIdentity.email, isIdentifiedID: true } }
        : {}),
      loaded: () => {
        if (process.env.NODE_ENV === 'development' || localStorage.getItem('testPostHog') === 'true') {
          console.log('[PostHog] Initialized (memory mode)');
        }
        applyConsentState();
        if (persistedIdentity) {
          identifyFromPersistedSession(persistedIdentity);
        }
      },
      // Session replay captures form inputs and scroll behavior, which counts as
      // personal data under GDPR/UK GDPR/CCPA. Start disabled and only turn on
      // after the user grants all-cookies consent (see applyConsentState).
      disable_session_recording: true,
      autocapture: false,
    });
  } catch (error) {
    console.warn('[PostHog] Failed to initialize (may be blocked by ad blocker):', error.message);
  }
};

/**
 * Apply consent state: upgrade to cookie persistence if consented, opt out if declined.
 * Session recording starts only on all-cookies consent.
 */
function applyConsentState() {
  const consentType = localStorage.getItem('cookieConsentType');
  if (consentType === 'all_cookies') {
    posthog.set_config({ persistence: 'localStorage+cookie' });
    posthog.opt_in_capturing();
    posthog.startSessionRecording();
  } else if (consentType === 'essential_only') {
    posthog.opt_out_capturing();
    posthog.stopSessionRecording();
  }
}

/**
 * Upgrade to cookie persistence after user consents. Call from CookieConsentBar on accept.
 */
export const upgradeToCookiePersistence = () => {
  if (posthog.__loaded) {
    posthog.set_config({ persistence: 'localStorage+cookie' });
    posthog.opt_in_capturing();
    posthog.startSessionRecording();
  }
};

/**
 * Opt out of capturing when user declines analytics. Call from CookieConsentBar on decline.
 */
export const optOutOfCapturing = () => {
  if (posthog.__loaded) {
    posthog.opt_out_capturing();
    posthog.stopSessionRecording();
  }
};

/**
 * Safely capture a PostHog event
 * Fires when PostHog is loaded and user has not opted out (memory mode before consent, or cookie after consent)
 * @param {string} eventName - The name of the event
 * @param {object} properties - Optional properties to include with the event
 */
export const trackEvent = (eventName, properties = {}) => {
  try {
    const isProduction = process.env.NODE_ENV === 'production';
    const isTestMode = localStorage.getItem('testPostHog') === 'true';
    
    if ((isProduction || isTestMode) && posthog.__loaded && posthog.has_opted_in_capturing()) {
      posthog.capture(eventName, properties);
      
      // Log in development/test mode for debugging
      if (isTestMode) {
        console.log(`[PostHog] Event tracked: ${eventName}`, properties);
      }
    }
  } catch (error) {
    // Silently fail - PostHog may be blocked
    console.warn(`[PostHog] Failed to track event: ${eventName}`, error.message);
  }
};

/**
 * Track a page view with custom properties
 * @param {string} pageName - Name of the page
 * @param {object} properties - Optional additional properties
 */
export const trackPageView = (pageName, properties = {}) => {
  trackEvent('$pageview', {
    page_name: pageName,
    ...properties,
  });
};

/**
 * Identify a brand-new user at account setup time, before they have a full profile.
 * Call after the account-setup form succeeds, when only email is known.
 * @param {string} email
 * @param {object} properties - Extra properties to set on the person
 */
export const identifyNewUser = (email, properties = {}) => {
  try {
    if (!email) return;
    const isProduction = process.env.NODE_ENV === 'production';
    const isTestMode = localStorage.getItem('testPostHog') === 'true';
    if ((isProduction || isTestMode) && posthog.__loaded && posthog.has_opted_in_capturing()) {
      posthog.identify(email, { email, ...properties });
      if (isTestMode) console.log('[PostHog] New user identified:', email);
    }
  } catch (error) {
    console.warn('[PostHog] Failed to identify new user:', error.message);
  }
};

/**
 * Identify user with enriched person properties for segmentation.
 * Call after login/signup when user data is available.
 * @param {object} user - User object from AuthContext/API
 */
export const identifyUser = (user) => {
  try {
    if (!user?.email) return;

    const isProduction = process.env.NODE_ENV === 'production';
    const isTestMode = localStorage.getItem('testPostHog') === 'true';

    if ((isProduction || isTestMode) && posthog.__loaded && posthog.has_opted_in_capturing()) {
      posthog.identify(user.email, {
        email: user.email,
        plan: user.subscription_status || 'none',
        signed_up_at: user.created_at || null,
        subscription_end_date: user.subscription_end_date || null,
        is_subscribed: user.is_subscribed || false,
        session_count: user.session_count || 0,
      });

      if (isTestMode) {
        console.log('[PostHog] User identified:', user.email);
      }
    }
  } catch (error) {
    console.warn('[PostHog] Failed to identify user:', error.message);
  }
};

// Export posthog instance for direct access when needed
export { posthog };
